/**
 * AvailabilityExceptionForm (BON-241) — the "Add a date" row of the Specific
 * dates section. The date input floors at today; past dates are immutable
 * server-side and the picker shouldn't offer them either.
 */
import { useState } from 'react';

import {
  AVAILABILITY_SLOTS,
  SLOT_LABELS,
  type AvailabilitySlot,
  type ExceptionEntry,
} from '../../../api/availability';

import { editableFloorKey, isEditableDate } from './availabilityModel';

export interface AvailabilityExceptionFormProps {
  /** Dates already taken — adding a duplicate is blocked with a hint. */
  takenDates: string[];
  onAdd: (entry: ExceptionEntry) => void;
  disabled: boolean;
}

const controlStyle: React.CSSProperties = { padding: '6px 8px', fontSize: 12 };

export function AvailabilityExceptionForm({
  takenDates,
  onAdd,
  disabled,
}: AvailabilityExceptionFormProps) {
  const [date, setDate] = useState('');
  const [state, setState] = useState<AvailabilitySlot>('off');
  const [note, setNote] = useState('');

  const floor = editableFloorKey();
  const duplicate = date !== '' && takenDates.includes(date);
  // `min` on a date input is advisory — a typed value below it is still
  // accepted and there's no form to run constraint validation. Without this
  // the row is added, then the whole save 400s on the server.
  const tooEarly = date !== '' && !isEditableDate(date);

  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="date"
          aria-label="Exception date"
          value={date}
          min={floor}
          disabled={disabled}
          onChange={(e) => setDate(e.target.value)}
          style={controlStyle}
        />
        <select
          aria-label="Exception availability"
          value={state}
          disabled={disabled}
          onChange={(e) => setState(e.target.value as AvailabilitySlot)}
          style={controlStyle}
        >
          {AVAILABILITY_SLOTS.map((slot) => (
            <option key={slot} value={slot}>
              {slot === 'off' ? 'Blocked off' : SLOT_LABELS[slot]}
            </option>
          ))}
        </select>
        <input
          type="text"
          aria-label="Reason (optional)"
          placeholder="Reason (optional)"
          value={note}
          maxLength={200}
          disabled={disabled}
          onChange={(e) => setNote(e.target.value)}
          style={{ ...controlStyle, flex: 1, minWidth: 140 }}
        />
        <button
          type="button"
          className="nb-btn"
          disabled={date === '' || duplicate || tooEarly || disabled}
          onClick={() => {
            onAdd({ date, state, note: note.trim() === '' ? null : note.trim() });
            setDate('');
            setNote('');
            setState('off');
          }}
        >
          Add a date
        </button>
      </div>
      {duplicate && (
        <div role="alert" style={{ fontSize: 11.5, color: 'var(--nb-warn)' }}>
          That date already has an override — remove it first to change it.
        </div>
      )}
      {tooEarly && (
        <div role="alert" style={{ fontSize: 11.5, color: 'var(--nb-warn)' }}>
          That date has already passed — pick today or later.
        </div>
      )}
    </>
  );
}
